import { alignMaxMatchCost, alignSkipCost, alignTranspositionPenalty, alignTranspositionRange } from './constants.js'
import type { SungNote } from './types.js'

export interface AlignedPair {
  /** Index into the sung note array. */
  sungIndex: number
  /** Index into the reference MIDI array. */
  referenceIndex: number
}

export interface Alignment {
  pairs: AlignedPair[]
  /** Indices into `reference` of notes the singer did not sing. */
  missed: number[]
  /** Indices into `sung` of notes that are not in the reference. */
  extra: number[]
  /**
   * The whole-phrase shift, in semitones, under which the notes were paired: −2 means the phrase
   * was sung a whole step low. It decides only which note is which; the pairs are scored against
   * the pitches actually sung (GH #2341).
   */
  transposition: number
}

type ShiftedAlignment = Omit<Alignment, 'transposition'> & { cost: number }

/** The note-sequence alignment with every sung pitch moved by `-shift` semitones first. */
const alignAtShift = (sung: SungNote[], reference: number[], shift: number): ShiftedAlignment => {
  /**
   * Align the sung note sequence to the reference sequence over the *note sequences*, not over
   * clock time (§5.3). Singers do not hold tempo, and this is a pitch tool — penalising a slow
   * singer for being slow would measure the wrong thing.
   *
   * The insertion and deletion paths are the point: they give the two error categories that
   * matter musically and are otherwise invisible — "you missed a note" and "you sang an extra
   * note".
   */
  const rows = sung.length
  const columns = reference.length

  const matchCost = (sungIndex: number, referenceIndex: number) =>
    Math.min(Math.abs(sung[sungIndex].midi - shift - reference[referenceIndex]), alignMaxMatchCost)

  /** cost[i][j] = cheapest alignment of the first i sung notes to the first j reference notes. */
  const cost: number[][] = Array.from({ length: rows + 1 }, () => new Array<number>(columns + 1).fill(0))
  for (let i = 1; i <= rows; i++) {
    cost[i][0] = i * alignSkipCost
  }
  for (let j = 1; j <= columns; j++) {
    cost[0][j] = j * alignSkipCost
  }
  for (let i = 1; i <= rows; i++) {
    for (let j = 1; j <= columns; j++) {
      cost[i][j] = Math.min(
        cost[i - 1][j - 1] + matchCost(i - 1, j - 1),
        cost[i - 1][j] + alignSkipCost,
        cost[i][j - 1] + alignSkipCost
      )
    }
  }

  const pairs: AlignedPair[] = []
  const missed: number[] = []
  const extra: number[] = []

  let i = rows
  let j = columns
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && cost[i][j] === cost[i - 1][j - 1] + matchCost(i - 1, j - 1)) {
      pairs.push({ sungIndex: i - 1, referenceIndex: j - 1 })
      i--
      j--
      continue
    }
    if (i > 0 && cost[i][j] === cost[i - 1][j] + alignSkipCost) {
      extra.push(i - 1)
      i--
      continue
    }
    missed.push(j - 1)
    j--
  }

  return { pairs: pairs.reverse(), missed: missed.reverse(), extra: extra.reverse(), cost: cost[rows][columns] }
}

/** 0, then ±1, ±2, …: on an exact tie the smaller shift was tried first and is kept */
const shiftsToTry = Array.from({ length: alignTranspositionRange }, (_, index) => [index + 1, -(index + 1)]).reduce(
  (shifts, pair) => [...shifts, ...pair],
  [0]
)

/**
 * Align the sung note sequence to the reference, over the *note sequences* and not over clock time
 * (§5.3).
 *
 * - Singers don't hold tempo, and this is a pitch tool; penalising a slow singer for being slow
 *   would measure the wrong thing.
 * - The insertion and deletion paths are the point: they give the two error categories that matter
 *   musically and are otherwise invisible, "you missed a note" and "you sang an extra note".
 *
 * **Which note is which does not depend on where the singer started (GH #2341).** A phrase sung
 * k semitones off is aligned under every shift in ±`alignTranspositionRange`. Each alignment costs
 * its own cost plus `alignTranspositionPenalty`·|k|, and the cheapest wins.
 *
 * - Without the search, C-D-E-D-C sung a whole step low paired sung C with reference C, one note
 *   along. It reported a missed and an extra note for a singer whose every interval was exact.
 * - The penalty keeps a phrase sung at pitch, with a wrong note or two, at 0.
 * - A one-note item has no shape to search over, so it is aligned at 0 only.
 */
export const alignNotes = (sung: SungNote[], reference: number[]): Alignment => {
  const shifts = sung.length < 2 || reference.length < 2 ? [0] : shiftsToTry
  let best: (ShiftedAlignment & { transposition: number; total: number }) | undefined
  for (const shift of shifts) {
    const aligned = alignAtShift(sung, reference, shift)
    const total = aligned.cost + alignTranspositionPenalty * Math.abs(shift)
    if (!best || total < best.total) {
      best = { ...aligned, transposition: shift, total }
    }
  }
  /** `shifts` always holds 0, so the loop ran at least once */
  const { pairs = [], missed = [], extra = [], transposition = 0 } = best ?? {}
  return { pairs, missed, extra, transposition }
}
